import React, { useEffect, useMemo } from 'react';
import { TTag } from './types';
import { TBaseTagContext } from '../types';
import { validate } from '@krugom/validator';
import { isFunction, isPlainObject, merge, reduce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { selectApiState } from '../../../store/selector/selectApiState';
import { fnv1aHash } from '../../../utils/fnv1aHash';
import { IApiRequestConfig } from '../../../store/saga/api/types';
import UniversalChildren from '../../UniversalTag/UniversalChildren';
import { ApiRequest } from '../../../store/saga/api/actions';
import { setApiState } from '../../../store/reducer';

const createKey = (reqProps: Partial<IApiRequestConfig>) => {
  try {
    const confString = JSON.stringify(reqProps);
    return fnv1aHash(confString);
  } catch {
    return 'unknown';
  }
};

export const CustomRequest: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const validationScheme = useMemo(
    () => ({
      type: 'object',
      properties: {
        request: {
          type: 'object',
          properties: {
            path: {
              type: 'string',
            },
            method: {
              type: 'string',
              mask: /^(GET)|(POST)|(PUT)|(DELETE)$/,
            },
            query: {
              type: 'custom',
              validator: (v: any) => isPlainObject(v),
            },
            body: {
              type: 'custom',
              validator: (v: any) => isPlainObject(v),
            },
            headers: {
              type: 'custom',
              validator: (v: any) => isPlainObject(v),
            },
            force: {
              type: 'boolean',
            },
            type: {
              type: 'string',
            },
          },
        },
      },
    }),
    []
  );

  const result = useMemo(() => validate(tagProps, validationScheme), [tagProps, validationScheme]);

  const key = useMemo(
    () => (result.isValid ? createKey(tagProps.request) : 'unknown'),
    [tagProps.request]
  );
  const dispatch = useDispatch();
  const requestState = useSelector((state: RootState) => selectApiState(state, key)) || {
    status: 'init',
  };
  useEffect(() => {
    if (requestState.status === 'succeed') dispatch(setApiState({ key }));
  }, [requestState.status, key, dispatch]);

  const handleSubmit = (handlerParams?: Partial<IApiRequestConfig>) => {
    const mergedParams = reduce(
      merge(tagProps.request, handlerParams, { key }),
      (r, v, k) => ({ ...r, ...(/^on[A-Z]/.test(k) ? {} : { [k]: v }) }),
      {}
    );
    const fullParamsSchema = {
      type: 'object',
      properties: {
        type: 'object',
        properties: {
          host: {
            type: 'string',
          },
          path: {
            type: 'string',
            required: true,
          },
          method: {
            type: 'string',
            mask: /^(GET)|(POST)|(PUT)|(DELETE)$/,
          },
          query: {
            type: 'custom',
            validator: (v: any) => isPlainObject(v),
          },
          body: {
            type: 'custom',
            validator: (v: any) => isPlainObject(v),
          },
          headers: {
            type: 'custom',
            validator: (v: any) => isPlainObject(v),
          },
          force: {
            type: 'boolean',
          },
          type: {
            type: 'string',
          },
        },
      },
    };
    const result = validate(mergedParams, fullParamsSchema);
    if (result.isValid) {
      dispatch(ApiRequest(mergedParams as IApiRequestConfig));
    }
  };

  useEffect(() => {
    if (requestState.status === 'succeed') {
      if (isFunction(tagProps.request.onSucceed)) tagProps.request.onSucceed();
      dispatch(setApiState({ key }));
    }
    if (requestState.status === 'rejected') {
      if (isFunction(tagProps.request.onRejected)) tagProps.request.onRejected();
      dispatch(setApiState({ key }));
    }
  }, [requestState.status, dispatch, key]);

  const requestContext = useMemo(
    () => ({
      ...requestState,
      key,
      handleSubmit,
    }),
    [key, requestState, handleSubmit]
  );

  const childrenContext = useMemo(
    () => ({
      ...context,
      request: requestContext,
    }),
    [context, requestContext]
  );

  if (!result.isValid) {
    console.error(
      'Тэг: $RequestContext;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }
  return <UniversalChildren context={childrenContext}>{tagProps.children}</UniversalChildren>;
});
CustomRequest.displayName = 'CustomRequest';
